import { Navigate, NavLink, useParams } from "react-router-dom";
import Profile from "../ui/Profile";
import TeamManagement from "./TeamManagement";
import ClinicTemplates from "./ClinicTemplates";
import ClinicQuickMessages from "./ClinicQuickMessages";
import ClinicClosures from "./ClinicClosures";
import ClinicBookingSettings from "./ClinicBookingSettings";
import { useUser } from "../lib/UserContext";
import { usePermission, usePermissions } from "../lib/usePermission";
import {
  buildClinicSettingsTabKeys,
  buildClinicSettingsPath,
  normalizeClinicSettingsTab,
} from "../lib/clinicSettings.mjs";

const SETTINGS_TABS = [
  {
    key: "profile",
    label: "Profile",
    description: "Account details, contract review, Gmail connection, and Chakra import.",
  },
  {
    key: "conversation-appearance",
    label: "Conversation appearance",
    description: "Visual defaults for the conversations list, starting with highlight colors.",
  },
  {
    key: "conversation-labels",
    label: "Conversation labels",
    description: "Manage the labels used to tag and filter conversations.",
  },
  {
    key: "blocked-answers",
    label: "Blocked answers",
    description: "Manage phone numbers for which the bot must not answer automatically.",
  },
  {
    key: "team",
    label: "Team",
    description: "Manage staff access, passwords, and the single Settings permission.",
  },
  {
    key: "templates",
    label: "Templates",
    description: "Edit the clinic WhatsApp templates shown in the backoffice.",
  },
  {
    key: "quick-messages",
    label: "Quick messages",
    description: "Create and edit shortcuts your team can insert in patient conversations.",
  },
  {
    key: "booking-availability",
    label: "Booking & availability",
    description: "Choose how many appointment times the bot proposes to patients.",
  },
  {
    key: "planned-closures",
    label: "Planned closures",
    description: "Manage exceptional closure dates that the bot must not offer.",
  },
] as const;

export default function Settings({
  enableQuickMessages = false,
}: {
  enableQuickMessages?: boolean;
}) {
  const user = useUser();
  const canAccessSettings = usePermission("manage_clinic_settings");
  const permissions = usePermissions();
  const params = useParams();

  if (user === undefined) {
    return <div className="p-6 text-gray-500">Loading settings…</div>;
  }

  if (user === null) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === "admin") {
    return <Navigate to="/profile" replace />;
  }

  if (!canAccessSettings) {
    return <Navigate to="/conversations" replace />;
  }

  const rawTab = params.tab;
  const tab = normalizeClinicSettingsTab(rawTab);
  const visibleTabKeys = buildClinicSettingsTabKeys({
    permissions,
    enableQuickMessages,
  });
  const visibleTabs = SETTINGS_TABS.filter((item) => visibleTabKeys.includes(item.key));

  if (!rawTab) {
    return <Navigate to={buildClinicSettingsPath("profile")} replace />;
  }

  if (rawTab !== tab) {
    return <Navigate to={buildClinicSettingsPath(tab)} replace />;
  }

  if (!visibleTabKeys.includes(tab)) {
    return <Navigate to={buildClinicSettingsPath("profile")} replace />;
  }

  const activeTab = visibleTabs.find((item) => item.key === tab) ?? visibleTabs[0];

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
          Clinic settings
        </p>
        <h1 className="text-3xl font-semibold text-slate-900">Settings</h1>
        <p className="max-w-3xl text-sm text-slate-600">{activeTab.description}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {visibleTabs.map((item) => (
          <NavLink
            key={item.key}
            to={buildClinicSettingsPath(item.key)}
            className={({ isActive }) =>
              `rounded-full border px-4 py-2 text-sm font-medium transition ${
                isActive
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900"
              }`
            }
          >
            {item.label}
          </NavLink>
        ))}
      </div>

      <div className="rounded-[28px] border border-slate-200 bg-slate-50/70 p-4 sm:p-6">
        {tab === "profile" && <Profile mode="settings-profile" />}
        {tab === "conversation-appearance" && <Profile mode="settings-appearance" />}
        {tab === "conversation-labels" && <Profile mode="settings-labels" />}
        {tab === "blocked-answers" && <Profile mode="settings-blocked-answers" />}
        {tab === "team" && <TeamManagement embedded />}
        {tab === "templates" && <ClinicTemplates embedded />}
        {tab === "quick-messages" && <ClinicQuickMessages embedded />}
        {tab === "booking-availability" && <ClinicBookingSettings />}
        {tab === "planned-closures" && <ClinicClosures />}
      </div>
    </div>
  );
}
